import classnames from "classnames"
import { createSelector, createStructuredSelector } from "reselect"

import {
    selectIsCustomDataSheet,
    selectIsPublicSheetMode,
    selectIsResourceTable,
    selectRowHeightFactor,
    selectSheetDataType,
    selectSheetId,
    selectTagTypeForCustomTagCell,
} from "sheets/selectors/sheet-selectors"

import {
    makeColumnPropertySelector,
    selectCellIsEditable,
    selectCellValue,
    selectCellValueIsNotEditable,
    selectColumnDataType,
    selectColumnId,
    selectColumnIsEditable,
    selectColumnType,
    selectFieldObject,
    selectIndividualColumnByIndex,
    selectIsClickedCell,
    selectIsDerived,
    selectIsEditing,
    selectResourceTableOptionalDisplayFunction,
    selectRowIdForCell,
    selectSheetCellLink,
    selectShouldShowEditableIcon,
    selectStateForCell,
    selectStateStyleForCell,
    selectStateStyleForMantineCell,
} from "app/static/frontend/sheets/selectors/column-selectors"

import {
    BillPriorityCell as MantineBillPriorityCell,
    BillStanceCell as MantineBillStanceCell,
    BirthMonthDayCell as MantineBirthMonthDayCell,
    CheckCell as MantineCheckCell,
    ContactAssignmentCell as MantineContactAssignmentCell,
    CookPVICell as MantineCookPVICell,
    CurrencyCell as MantineCurrencyCell,
    CustomDataPersonsCell as MantineCustomDataPersonsCell,
    CustomFieldFromNoteCell as MantineCustomFieldFromNoteCell,
    CustomTagCell as MantineCustomTagCell,
    DateAndTimeCell as MantineDateAndTimeCell,
    DateCell as MantineDateCell,
    DefaultCell as MantineDefaultCell,
    EnumCell as MantineEnumCell,
    InteractionCell as MantineInteractionCell,
    IssueSelectCell as MantineIssueSelectCell,
    LargeTextCell as MantineLargeTextCell,
    LedgerAccountTypeCell as MantineLedgerAccountTypeCell,
    LinkCell as MantineLinkCell,
    MembershipCell as MantineMembershipCell,
    MentionableDefaultCell as MantineMentionableDefaultCell,
    PercentageCell as MantinePercentageCell,
    PublicOrganizationCell as MantinePublicOrganizationCell,
    RSVPStatusCell as MantineRSVPStatusCell,
    SearchStatCell as MantineSearchStatCell,
    SponsorCell as MantineSponsorCell,
    StafferSelectCell as MantineStafferSelectCell,
    StakeholderAlignmentCell as MantineStakeholderAlignmentCell,
    StanceCell as MantineStanceCell,
    StatCell as MantineStatCell,
    ThumbsUpDownNeutralCell as MantineThumbsUpDownNeutralCell,
    UserAssignmentCell as MantineUserAssignmentCell,
    UserAssignmentSingleCell as MantineUserAssignmentSingleCell,
    VoteCell as MantineVoteCell,
    YearCell as MantineYearCell,
} from "app/static/frontend/sheets/components/cells/MantineCells"
import {
    ContactAssignmentCell,
    UserAssignmentCell,
    UserAssignmentSingleCell,
} from "sheets/components/cells/AssignmentCell"
import BillPriorityCell from "sheets/components/cells/BillPriorityCell"
import BillStanceCell from "sheets/components/cells/BillStanceCell"
import BirthMonthDayCell from "sheets/components/cells/BirthMonthDayCell"
import CheckCell from "sheets/components/cells/CheckCell"
import CookPVICell from "sheets/components/cells/CookPVICell"
import CustomTagCell from "sheets/components/cells/CustomTagCell"
import DateAndTimeCell from "sheets/components/cells/DateAndTimeCell"
import DateCell from "sheets/components/cells/DateCell"
import DefaultCell from "sheets/components/cells/DefaultCell"
import EnumCell from "sheets/components/cells/EnumCell"
import InteractionCell from "sheets/components/cells/InteractionCell"
import IssueSelectCell from "sheets/components/cells/IssueSelectCell"
import LargeTextCell from "sheets/components/cells/LargeTextCell"
import LinkCell from "sheets/components/cells/LinkCell"
import MembershipCell from "sheets/components/cells/MembershipCell"
import PercentageCell from "sheets/components/cells/PercentageCell"
import SearchStatCell from "sheets/components/cells/SearchStatCell"
import SponsorCell from "sheets/components/cells/SponsorCell"
import StafferSelectCell from "sheets/components/cells/StafferSelectCell"
import StakeholderAlignmentCell from "sheets/components/cells/StakeholderAlignmentCell"
import StanceCell from "sheets/components/cells/StanceCell"
import StatCell from "sheets/components/cells/StatCell"
import VoteCell from "sheets/components/cells/VoteCell"
import YearCell from "sheets/components/cells/YearCell"
import CustomDataPersonsCell from "sheets/components/cells/CustomDataPersonsCell"
import CustomFieldFromNoteCell from "sheets/components/cells/CustomFieldFromNoteCell"
import RSVPStatusCell from "sheets/components/cells/RSVPStatusCell"
import MentionableDefaultCell from "sheets/components/cells/MentionableDefaultCell"
import LedgerAccountTypeCell from "sheets/components/cells/LedgerAccountTypeCell"
import ThumbsUpDownNeutralCell from "sheets/components/cells/ThumbsUpDownNeutralCell"
import CurrencyCell from "sheets/components/cells/CurrencyCell"
import PublicOrganizationCell from "sheets/components/cells/PublicOrganizationCell"

// this selector returns the Cell component to render
export const selectCellComponent = createSelector(
    selectColumnType,
    selectColumnDataType,
    selectFieldObject,
    selectIsResourceTable,
    makeColumnPropertySelector("optionalResourceTableCell"),
    (columnType, columnDataType, field, isResourceTable, optionalResourceTableCell) => {
        // some columns are configured via the automatic field detection system
        // these can be both ResourceTable columns as well as Sheet columns
        if (field) {
            if (field._is_enum_field) {
                return EnumCell
            }
            if (!columnDataType) {
                switch (field.field_type) {
                    case DjangIO.djangio.types.FieldType.DateField.value:
                        return DateCell
                    case DjangIO.djangio.types.FieldType.QuorumDateTimeField.value:
                        return DateAndTimeCell
                }
            }
        }

        if (isResourceTable && optionalResourceTableCell) {
            if (typeof optionalResourceTableCell !== "function") {
                console.warn(
                    "Must provide a function (an exported component will suffice) to render this cell. Ignoring & Render the default",
                )
                return DefaultCell
            }
            return optionalResourceTableCell
        }

        // if the column can be configured to have search_filters, it can be clicked to segue to Searchify
        if (columnType && DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).search_filters) {
            return SearchStatCell
        }
        switch (columnType) {
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.contact_organizations.value:
                return PublicOrganizationCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_assignment.value:
                return UserAssignmentCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment_primary.value:
                return UserAssignmentSingleCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_assignment.value:
                return ContactAssignmentCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer.value:
                return StafferSelectCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
                return CustomTagCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_list.value:
                return CheckCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_office.value:
                return DateAndTimeCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_date_only.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_office_date_only.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.next_interaction.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.date_of_last_public_mention.value:
                return DateCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.person_stat.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.project_stats.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.document_mention.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sum_of_columns.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data.value:
                return StatCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.percentage_of_columns.value:
                return PercentageCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.committee_membership.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.caucus_membership.value:
                return MembershipCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.voting_record.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.vote_direction.value:
                return VoteCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sponsorship_record.value:
                return SponsorCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_public_organization.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_office.value:
                return InteractionCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.vote_stance.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stance_record.value:
                return StanceCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_stance.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_stance.value:
                return BillStanceCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_priority.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_priority.value:
                return BillPriorityCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.cook_pvi.value:
                return CookPVICell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.member_birth_month_day.value:
                return BirthMonthDayCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.district_number.value:
                return DefaultCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.description.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.most_recent_description.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.most_recent_bill_ai_summary.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_text.value:
                return LargeTextCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_senator.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_representative.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_senator.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_representative.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_councilmember.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_delegate.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_assemblymember.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_county_legislator.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_municipal_legislator.value:
                return CustomDataPersonsCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
                return CustomFieldFromNoteCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.rsvp_status_of_custom_event.value:
                return RSVPStatusCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.in_issue.value:
                return IssueSelectCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_text.value:
                return MentionableDefaultCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_thumb.value:
                return ThumbsUpDownNeutralCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value:
                return StakeholderAlignmentCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_interaction_type.value:
                return InteractionCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.transaction_credit_ledger_account_type.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.transaction_debit_ledger_account_type.value:
                return LedgerAccountTypeCell
            default:
            // continue on - try to match on columnDataType
        }
        switch (columnDataType) {
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
                return StatCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
                return CheckCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
                return DateCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
                return YearCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.link.value:
                return LinkCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value:
                return CurrencyCell
            default:
                return DefaultCell
        }
    },
)

// This selector is identical to the one above, but we'll be using updated cell components
export const selectMantineCellComponent = createSelector(
    selectColumnType,
    selectColumnDataType,
    selectFieldObject,
    selectIsResourceTable,
    makeColumnPropertySelector("optionalResourceTableCell"),
    (columnType, columnDataType, field, isResourceTable, optionalResourceTableCell) => {
        // some columns are configured via the automatic field detection system
        // these can be both ResourceTable columns as well as Sheet columns
        if (field) {
            if (field._is_enum_field) {
                return MantineEnumCell
            }
            if (!columnDataType) {
                switch (field.field_type) {
                    case DjangIO.djangio.types.FieldType.DateField.value:
                        return MantineDateCell
                    case DjangIO.djangio.types.FieldType.QuorumDateTimeField.value:
                        return MantineDateAndTimeCell
                    default:
                }
            }
        }

        // // if the column can be configured to have search_filters, it can be clicked to segue to Searchify
        if (columnType && DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).search_filters) {
            return MantineSearchStatCell
        }
        switch (columnType) {
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.contact_organizations.value:
                return MantinePublicOrganizationCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_assignment.value:
                return MantineUserAssignmentCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment_primary.value:
                return MantineUserAssignmentSingleCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_assignment.value:
                return MantineContactAssignmentCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer.value:
                return MantineStafferSelectCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
                return MantineCustomTagCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_list.value:
                return MantineCheckCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_office.value:
                return MantineDateAndTimeCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_date_only.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_office_date_only.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.next_interaction.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.date_of_last_public_mention.value:
                return MantineDateCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.person_stat.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.project_stats.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.document_mention.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sum_of_columns.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data.value:
                return MantineStatCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.percentage_of_columns.value:
                return MantinePercentageCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.committee_membership.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.caucus_membership.value:
                return MantineMembershipCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.voting_record.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.vote_direction.value:
                return MantineVoteCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sponsorship_record.value:
                return MantineSponsorCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_public_organization.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_office.value:
                return MantineInteractionCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.vote_stance.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stance_record.value:
                return MantineStanceCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_stance.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_stance.value:
                return MantineBillStanceCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_priority.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_priority.value:
                return MantineBillPriorityCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.cook_pvi.value:
                return MantineCookPVICell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.member_birth_month_day.value:
                return MantineBirthMonthDayCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.district_number.value:
                return MantineDefaultCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.description.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.most_recent_description.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.most_recent_bill_ai_summary.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_text.value:
                return MantineLargeTextCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_senator.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_representative.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_senator.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_representative.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_councilmember.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_delegate.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_assemblymember.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_county_legislator.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_municipal_legislator.value:
                return MantineCustomDataPersonsCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
                return MantineCustomFieldFromNoteCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.rsvp_status_of_custom_event.value:
                return MantineRSVPStatusCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.in_issue.value:
                return MantineIssueSelectCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_text.value:
                return MantineMentionableDefaultCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_thumb.value:
                return MantineThumbsUpDownNeutralCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value:
                return MantineStakeholderAlignmentCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_interaction_type.value:
                return MantineInteractionCell
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.transaction_credit_ledger_account_type.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.transaction_debit_ledger_account_type.value:
                return MantineLedgerAccountTypeCell
            default:
            // continue on - try to match on columnDataType
        }
        switch (columnDataType) {
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.percent.value:
                return MantineStatCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
                return MantineCheckCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
                return MantineDateCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
                return MantineYearCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.link.value:
                return MantineLinkCell
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value:
                return MantineCurrencyCell
            default:
                return MantineDefaultCell
        }
    },
)

// LargeTextCell's get more specific styling
export const selectCellComponentClassName = createSelector(
    selectCellComponent,
    selectRowHeightFactor,
    selectTagTypeForCustomTagCell,
    selectStateStyleForCell,
    (CellComponent, rowHeightFactor, tagType, stateStyleForCell) => {
        let classes = ["sheet-cell"]
        if (
            CellComponent.prototype === LargeTextCell.prototype ||
            tagType === DjangIO.app.person.types.TagType.string.value
        ) {
            const largeTextCellClasses = ["large-text-cell-overflow-hidden", `large-text-cell-${rowHeightFactor || 1}`]
            classes = [...classes, ...largeTextCellClasses]
        }
        // we'd like to apply a style for cells that will get a coloring via javascript
        if (stateStyleForCell && Object.keys(stateStyleForCell).length) {
            return classnames([...classes, "colored-cell"])
        }
        return classnames(classes)
    },
)

// MantineLargeTextCell's get more specific styling
export const selectMantineCellComponentClassName = createSelector(selectStateStyleForCell, (stateStyleForCell) => {
    let classes = ["sheet-cell"]

    // we'd like to apply a style for cells that will get a coloring via javascript
    if (stateStyleForCell && Object.keys(stateStyleForCell).length) {
        return classnames([...classes, "colored-cell"])
    }
    return classnames(classes)
})

/**
 * Returns a list of ids from the column data if it exists, if it doesn't returns an empty array.
 * This format specifically applies to columns that generate non-scalar types. For more info:
 * https://docs.google.com/document/d/1z4VFCMMAh9M6lLZoG9x_gq79vKVuxUWJtSEameFvurA/edit#heading=h.kxv8h8w8el65
 *
 * @param {() => Object} selectIndividualColumnByIndex - an object containing information about a column
 * @param {() => Number}  selectRowIdForCell - the id of the row
 * @returns {Array<Number>} - an array of org ids
 */
export const selectIdsFromCellData = createSelector(
    selectIndividualColumnByIndex,
    selectRowIdForCell,
    (column, rowIdForCell) => {
        const rowId = String(rowIdForCell) || rowIdForCell
        const cellData = column && column.getIn(["data", rowId, "data"])

        return cellData ? cellData.map((i) => i.get("id")).toJS() : []
    },
)

// this connection is used to provide the wrapping Cell component props
export const cellConnection = createStructuredSelector({
    CellComponent: selectCellComponent,
    cellComponentClassName: selectCellComponentClassName,
    cellIsEditable: selectCellIsEditable,
    cellState: selectStateForCell,
    cellStyle: selectStateStyleForCell,
    cellValue: selectCellValue,
    cellValueIsNotEditable: selectCellValueIsNotEditable,
    columnId: selectColumnId,
    columnIsEditable: selectColumnIsEditable,
    dataType: selectSheetDataType,
    derived: selectIsDerived,
    editing: selectIsEditing,
    field: selectFieldObject,
    hasOptionalDisplayFunction: selectResourceTableOptionalDisplayFunction,
    idsFromCellData: selectIdsFromCellData,
    isClickedCell: selectIsClickedCell,
    isCustomDataSheet: selectIsCustomDataSheet,
    isPublicSheet: selectIsPublicSheetMode,
    isResourceTable: selectIsResourceTable,
    rowHeightFactor: selectRowHeightFactor,
    rowId: selectRowIdForCell,
    sheetDataType: selectSheetDataType,
    sheetKey: selectSheetId,
    shouldShowEditableIcon: selectShouldShowEditableIcon,
    cellLink: selectSheetCellLink,
})

// a split-off cellConnection for the new sheet to incrementally
// minimize reliance on redux.
export const mantineCellConnection = createStructuredSelector({
    CellComponent: selectMantineCellComponent,
    cellComponentClassName: selectMantineCellComponentClassName,
    cellIsEditable: selectCellIsEditable,
    cellState: selectStateForCell,
    cellStyle: selectStateStyleForMantineCell,
    cellValueIsNotEditable: selectCellValueIsNotEditable,
    columnIsEditable: selectColumnIsEditable,
    derived: selectIsDerived,
    editing: selectIsEditing,
    field: selectFieldObject,
    isClickedCell: selectIsClickedCell,
    isCustomDataSheet: selectIsCustomDataSheet,
    rowHeightFactor: selectRowHeightFactor,
    sheetKey: selectSheetId,
    shouldShowEditableIcon: selectShouldShowEditableIcon,
    cellLink: selectSheetCellLink,
})

// a selector to leave out `cellValue` because it is derived from a grouping value
export const mantineGroupedCellConnection = createStructuredSelector({
    CellComponent: selectMantineCellComponent,
    cellComponentClassName: selectMantineCellComponentClassName,
    cellIsEditable: () => false,
    cellState: selectStateForCell,
    cellStyle: selectStateStyleForMantineCell,
    cellValueIsNotEditable: selectCellValueIsNotEditable,
    columnId: selectColumnId,
    columnIsEditable: selectColumnIsEditable,
    dataType: selectSheetDataType,
    editing: () => false,
    field: selectFieldObject,
    isClickedCell: selectIsClickedCell,
    sheetDataType: selectSheetDataType,
    sheetKey: selectSheetId,
    shouldShowEditableIcon: selectShouldShowEditableIcon,
    cellLink: selectSheetCellLink,
    columnDataType: selectColumnDataType,
})

export const publicOrgsConnection = createStructuredSelector({
    ...cellConnection,
    idsFromCellData: selectIdsFromCellData,
})
